import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency } from "@/lib/api";
import { FileText } from "lucide-react";

export default function FarPage() {
  const { activeEntity, entities } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState(activeEntity?.id || "");

  useEffect(() => {
    if (activeEntity) setEntityFilter(activeEntity.id);
  }, [activeEntity]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (entityFilter) params.entity_id = entityFilter;
        const { data: d } = await api.get("/far/afs-note", { params });
        setData(d);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [entityFilter]);

  return (
    <div className="space-y-6" data-testid="far-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <FileText size={24} className="inline mr-2 text-[#C8A961]" />
            Full FAR - AFS Note
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">IAS 16.73 Property, Plant and Equipment Reconciliation</p>
        </div>
        <select className="stratum-input" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} data-testid="far-entity-filter">
          <option value="">All Entities (Consolidated)</option>
          {entities.map(ent => <option key={ent.id} value={ent.id}>{ent.name}</option>)}
        </select>
      </div>

      {loading || !data ? (
        <div className="stratum-card p-8 text-center text-[#6B7280]">Loading reconciliation...</div>
      ) : (
        <>
          {/* Main Reconciliation Table */}
          <div className="stratum-card overflow-hidden">
            <div className="p-4 border-b border-[#E8E2D5] bg-[#FAF8F4]">
              <h3 className="font-display text-lg font-semibold text-[#0B1020]">
                Property, Plant and Equipment - Reconciliation
              </h3>
              <p className="text-xs text-[#6B7280] mt-1">For the period ended {data.period}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="far-reconciliation-table">
                <thead>
                  <tr className="bg-[#0B1020] text-white text-xs">
                    <th className="px-3 py-2.5 text-left font-medium" rowSpan={2}>Category</th>
                    <th className="px-2 py-1.5 text-center font-medium border-l border-white/20" colSpan={4}>Cost</th>
                    <th className="px-2 py-1.5 text-center font-medium border-l border-white/20" colSpan={4}>Accumulated Depreciation</th>
                    <th className="px-2 py-1.5 text-center font-medium border-l border-white/20" colSpan={2}>Carrying Amount</th>
                  </tr>
                  <tr className="bg-[#1A233A] text-white/70 text-[10px] uppercase tracking-wider">
                    <th className="px-2 py-1.5 text-right border-l border-white/10">Opening</th>
                    <th className="px-2 py-1.5 text-right">Additions</th>
                    <th className="px-2 py-1.5 text-right">Disposals</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Closing</th>
                    <th className="px-2 py-1.5 text-right border-l border-white/10">Opening</th>
                    <th className="px-2 py-1.5 text-right">Charge</th>
                    <th className="px-2 py-1.5 text-right">Disposals</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Closing</th>
                    <th className="px-2 py-1.5 text-right border-l border-white/10">Opening</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {data.reconciliation.map((r, idx) => (
                    <tr key={r.category_id} className={idx % 2 === 0 ? "bg-white" : "bg-[#FAF8F4]"} data-testid={`far-row-${r.category_id}`}>
                      <td className="px-3 py-2 text-[#1A1A1A] font-medium text-xs">{r.category_name}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs border-l border-[#E8E2D5]">{formatCurrency(r.opening_cost)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs text-[#065F46]">{r.additions_cost > 0 ? formatCurrency(r.additions_cost) : "-"}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs text-[#991B1B]">{r.disposals_cost > 0 ? `(${formatCurrency(r.disposals_cost)})` : "-"}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs font-semibold">{formatCurrency(r.closing_cost)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs border-l border-[#E8E2D5]">{formatCurrency(r.opening_accum)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs text-[#991B1B]">{r.charge_accum > 0 ? `(${formatCurrency(r.charge_accum)})` : "-"}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs text-[#065F46]">{r.disposals_accum > 0 ? formatCurrency(r.disposals_accum) : "-"}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs font-semibold">{formatCurrency(r.closing_accum)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs border-l border-[#E8E2D5]">{formatCurrency(r.opening_carrying)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-xs font-bold text-[#0B1020]">{formatCurrency(r.closing_carrying)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#0B1020] text-white font-semibold text-xs">
                    <td className="px-3 py-2.5">Total</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums border-l border-white/20">{formatCurrency(data.totals.opening_cost)}</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums">{formatCurrency(data.totals.additions_cost)}</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums">{data.totals.disposals_cost > 0 ? `(${formatCurrency(data.totals.disposals_cost)})` : "-"}</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums">{formatCurrency(data.totals.closing_cost)}</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums border-l border-white/20">{formatCurrency(data.totals.opening_accum)}</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums">{data.totals.charge_accum > 0 ? `(${formatCurrency(data.totals.charge_accum)})` : "-"}</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums">{data.totals.disposals_accum > 0 ? formatCurrency(data.totals.disposals_accum) : "-"}</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums">{formatCurrency(data.totals.closing_accum)}</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums border-l border-white/20">{formatCurrency(data.totals.opening_carrying)}</td>
                    <td className="px-2 py-2.5 text-right font-mono tabular-nums text-[#C8A961]">{formatCurrency(data.totals.closing_carrying)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="stratum-card p-5">
              <span className="text-xs text-[#6B7280] uppercase tracking-wider">Total Cost</span>
              <div className="font-mono text-xl font-semibold text-[#0B1020] tabular-nums mt-2">{formatCurrency(data.totals.closing_cost)}</div>
            </div>
            <div className="stratum-card p-5">
              <span className="text-xs text-[#6B7280] uppercase tracking-wider">Total Accum Depreciation</span>
              <div className="font-mono text-xl font-semibold text-[#991B1B] tabular-nums mt-2">({formatCurrency(Math.abs(data.totals.closing_accum))})</div>
            </div>
            <div className="stratum-card p-5">
              <span className="text-xs text-[#6B7280] uppercase tracking-wider">Total Carrying Amount</span>
              <div className="font-mono text-xl font-semibold text-[#0B1020] tabular-nums mt-2">{formatCurrency(data.totals.closing_carrying)}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
